'use client';

import { useSidebar } from '@/components/ui/sidebar';
import { useCallback, useEffect, useRef } from 'react';
import {
  readResultsSidebarPreference,
  resolveResultsSidebarOpen,
  writeResultsSidebarPreference
} from '../utils/results-sidebar';

/**
 * OMA-624 — collapse the nav sidebar while a results screen is open, offer a
 * toggle, and remember the operator's answer.
 *
 * `setOpen` writes the app-wide `sidebar_state` cookie, so this BORROWS the
 * global state: it stashes the incoming value on mount and restores it on
 * unmount. Without that, opening one container would leave every other
 * dashboard screen collapsed for the rest of the session — a screen-local
 * layout choice must not become a global one.
 *
 * `enabled` rather than a conditional call site: the panel renders on the QA
 * and supervisor surfaces too, and a reviewer's sidebar is not this feature's
 * business. Hooks cannot be called conditionally, so the flag gates the
 * effects instead.
 */
export function useResultsSidebar(enabled: boolean): {
  open: boolean;
  toggle: () => void;
} {
  const { open, setOpen } = useSidebar();

  // shadcn's provider rebuilds `setOpen` whenever `open` changes — its
  // `useCallback` closes over `open` to resolve the updater form. So it cannot
  // be a dependency of the borrow effect below: every toggle would tear that
  // effect down and re-apply the stored preference, snapping the sidebar shut
  // one frame after the header's `SidebarTrigger` opened it. That is exactly
  // how the trigger read as dead on this screen. The ref keeps the effect's
  // identity stable while still calling the current setter.
  const setOpenRef = useRef(setOpen);

  useEffect(() => {
    setOpenRef.current = setOpen;
  }, [setOpen]);

  // The value to hand back on the way out. Captured through a ref so the
  // restore effect does not re-run every time `open` changes — and behind a
  // `didInit` guard because StrictMode mounts twice in dev, and a second
  // capture would record the value we ourselves just wrote.
  const incomingOpen = useRef(open);
  const didInit = useRef(false);

  // What this hook put on screen on entry, and whether that value has actually
  // landed. Together they tell an operator's toggle apart from the borrowed
  // value this screen arrived with — only the former is worth remembering.
  const appliedOpen = useRef<boolean | null>(null);
  const settled = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    if (!didInit.current) {
      didInit.current = true;
      incomingOpen.current = open;
      const initial = resolveResultsSidebarOpen(readResultsSidebarPreference());
      appliedOpen.current = initial;
      settled.current = false;
      setOpenRef.current(initial);
    }

    return () => {
      didInit.current = false;
      appliedOpen.current = null;
      settled.current = false;
      setOpenRef.current(incomingOpen.current);
    };
    // `open` is read once behind the guard, deliberately not a dependency:
    // re-running this on every toggle would fight the operator's own choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Remember whichever control flipped it. This screen renders no toggle of its
  // own — the app header's `SidebarTrigger` is the only one on offer — so
  // persisting from this hook's `toggle` alone remembered nothing at all, and
  // every visit re-collapsed the sidebar of an operator who had asked for it
  // back.
  useEffect(() => {
    if (!enabled || appliedOpen.current === null) return;

    if (!settled.current) {
      // The pass in which the borrow effect ran still sees the value the screen
      // was entered with: the state update it queued has not rendered yet.
      // Writing then would persist the app-wide value merely borrowed here.
      if (open === appliedOpen.current) settled.current = true;
      return;
    }

    writeResultsSidebarPreference(open);
  }, [enabled, open]);

  const toggle = useCallback(() => {
    const next = !open;
    writeResultsSidebarPreference(next);
    setOpen(next);
  }, [open, setOpen]);

  return { open, toggle };
}
